import { PopoverProps } from "@/types/component";

import "./index.scss";

import React, { useRef, useState, useLayoutEffect, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

function getScrollParents(element: HTMLElement): (HTMLElement | Window)[] {
  const parents: (HTMLElement | Window)[] = [];
  let parent: HTMLElement | null = element.parentElement;
  while (parent) {
    const style = getComputedStyle(parent);
    const overflow = style.overflow + style.overflowX + style.overflowY;
    if (/(auto|scroll|overlay)/.test(overflow)) {
      parents.push(parent);
    }
    parent = parent.parentElement;
  }
  parents.push(window);
  return parents;
}

const Popover: React.FC<PopoverProps> = ({
  children,
  content,
  isOpen,
  onClose,
  position = "top",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );
  const [flipped, setFlipped] = useState(false);

  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const gap = 4;
    const contentHeight = contentRef.current?.offsetHeight ?? 200;
    const contentWidth = contentRef.current?.offsetWidth ?? 160;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const useTop =
      position === "bottom" &&
      spaceBelow < contentHeight + gap &&
      spaceAbove > spaceBelow;
    setFlipped(useTop);
    let top = 0;
    let left = rect.left + rect.width / 2;
    if (useTop) {
      top = rect.top - gap;
    } else if (position === "top") {
      top = rect.top - gap;
    } else if (position === "bottom") {
      top = rect.bottom + gap;
    } else if (position === "right") {
      top = rect.top + rect.height / 2;
      left = rect.right + gap;
    } else if (position === "left") {
      top = rect.top + rect.height / 2;
      left = rect.left - gap;
    } else {
      top = rect.bottom + gap;
    }
    // Keep popover within viewport when centered (bottom/top)
    if (position === "bottom" || position === "top" || useTop) {
      const halfWidth = contentWidth / 2;
      if (left - halfWidth < gap) {
        left = halfWidth + gap;
      } else if (left + halfWidth > viewportWidth - gap) {
        left = viewportWidth - halfWidth - gap;
      }
    }
    setCoords({ top, left });
  }, [position]);

  useLayoutEffect(() => {
    if (!isOpen || !containerRef.current) {
      setCoords(null);
      setFlipped(false);
      return;
    }
    updatePosition();
    // Re-calculate after content is rendered to get actual height
    requestAnimationFrame(() => {
      updatePosition();
    });
  }, [isOpen, position, updatePosition]);

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;
    const scrollParents = getScrollParents(containerRef.current);
    const handleScrollOrResize = () => updatePosition();
    scrollParents.forEach((el) => {
      el.addEventListener("scroll", handleScrollOrResize, { passive: true });
    });
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      scrollParents.forEach((el) => {
        el.removeEventListener("scroll", handleScrollOrResize);
      });
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current?.contains(target) ||
        contentRef.current?.contains(target)
      ) {
        return;
      }
      onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const getTransform = () => {
    if (flipped || position === "top") return "translateX(-50%) translateY(-100%)";
    if (position === "bottom") return "translateX(-50%)";
    if (position === "right" || position === "left") return "translateY(-50%)";
    return undefined;
  };

  const effectivePosition = flipped ? "top" : position;

  const popoverContent =
    isOpen && coords ? (
      <div
        ref={contentRef}
        className={`popover popover-${effectivePosition} popover--portal`}
        style={{
          position: "fixed",
          left: coords.left,
          top: coords.top,
          transform: getTransform(),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div onClick={onClose}>{content}</div>
      </div>
    ) : null;

  return (
    <div ref={containerRef} className="popover-container">
      {children}
      {popoverContent && createPortal(popoverContent, document.body)}
    </div>
  );
};

export default Popover;
